import React from 'react';
import { Check, Sparkles, Zap, Shield, Crown, ArrowRight, Info } from 'lucide-react';
import { motion } from 'motion/react';
import { SubscriptionPlan } from '../types';

interface PricingProps {
  onSelectPlan: (plan: SubscriptionPlan) => void;
  currentPlan?: SubscriptionPlan;
}

const PricingPanel: React.FC<PricingProps> = ({ onSelectPlan, currentPlan }) => {
  const plans = [
    {
      id: SubscriptionPlan.FREE_TRIAL,
      name: 'Teste Gratuito',
      price: 'Beta aberta',
      period: 'sem cobrança',
      description: 'Aproveite todos os recursos durante a fase de testes.',
      features: [
        'Gravação e Transcrição ILIMITADA',
        'Análise Clínica Completa via IA',
        'Dashboard de Evolução',
        'Repositório de Documentos',
        'Suporte prioritário via E-mail'
      ],
      icon: <Zap className="text-amber-400" size={24} />,
      buttonText: 'Explorar Versão Beta',
      highlight: false
    },
    {
      id: SubscriptionPlan.MONTHLY,
      name: 'Plano Mensal',
      price: 'Sob consulta',
      period: 'contratação assistida',
      description: 'Converse com nossa equipe para definir o melhor formato para seu consultório.',
      features: [
        'Tudo do Teste Gratuito',
        'Acesso Contínuo e Seguro',
        'Cobrança recorrente automática',
        'Cancelamento a qualquer momento'
      ],
      icon: <Shield className="text-indigo-400" size={24} />,
      buttonText: 'Falar com a equipe',
      highlight: false,
      onClick: () => window.open('https://wa.me/5554981115362?text=Olá,%20quero%20assinar%20o%20Allodos%20mensalmente.', '_blank')
    },
    {
      id: SubscriptionPlan.ANNUAL,
      name: 'Plano Anual',
      price: 'Sob consulta',
      period: 'contratação assistida',
      description: 'Condições comerciais personalizadas conforme o momento da contratação.',
      features: [
        'Tudo do Plano Mensal',
        'Condições especiais para contratação anual',
        'Prioridade em novos recursos'
      ],
      icon: <Crown className="text-yellow-400" size={24} />,
      buttonText: 'Solicitar condições',
      highlight: true,
      badge: 'Condições Especiais',
      onClick: () => window.open('https://wa.me/5554981115362?text=Olá,%20quero%20conhecer%20as%20condições%20do%20plano%20anual%20do%20Allodos.', '_blank')
    }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-10 sm:py-20 font-sans">
      <div className="text-center mb-16 space-y-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-500/10 border border-indigo-500/20 rounded-full text-indigo-400 text-[10px] font-black uppercase tracking-[0.2em]"
        >
          <Sparkles size={14} /> Investimento em Tecnologia Clínica
        </motion.div>
        <motion.h2 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-3xl sm:text-4xl md:text-5xl font-black text-slate-950 dark:text-white tracking-tighter text-center"
        >
          Escolha o Plano Ideal
        </motion.h2>
        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto text-sm font-medium leading-relaxed"
        >
          Transparência total e foco no que importa: o cuidado com seus pacientes. Escolha a melhor opção para escalar seu consultório com Inteligência Artificial.
        </motion.p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
        {plans.map((plan, index) => (
          <motion.div
            key={plan.id}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 * (index + 3) }}
            className={`flex flex-col relative bg-slate-900 border ${
              plan.highlight 
                ? 'border-indigo-500 border-2 shadow-[0_0_50px_-12px_rgba(79,70,229,0.3)] z-10 scale-105' 
                : 'border-slate-800'
            } rounded-[2rem] sm:rounded-[3.5rem] p-5 sm:p-10 transition-all hover:bg-slate-900/80 group`}
          >
            {plan.badge && (
              <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-indigo-600 text-white px-6 py-2 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl">
                {plan.badge}
              </div>
            )}

            <div className="mb-8">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 ${plan.highlight ? 'bg-indigo-600/20' : 'bg-slate-800'}`}>
                {plan.icon}
              </div>
              <h3 className="text-xl font-black text-white tracking-tight mb-2 uppercase tracking-[0.1em]">{plan.name}</h3>
              <p className="text-xs text-slate-500 font-medium leading-relaxed mb-6">{plan.description}</p>
              
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-3xl font-black text-white tracking-tighter">{plan.price}</span>
                <span className="text-slate-500 text-xs font-black uppercase tracking-widest">{plan.period}</span>
              </div>
            </div>

            <div className="flex-1 space-y-4 mb-10">
              {plan.features.map((feature, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className={`mt-0.5 shrink-0 w-5 h-5 rounded-lg flex items-center justify-center ${plan.highlight ? 'bg-indigo-500/20 text-indigo-400' : 'bg-slate-800 text-slate-500'}`}>
                    <Check size={12} strokeWidth={4} />
                  </div>
                  <span className="text-xs text-slate-300 font-medium leading-tight">{feature}</span>
                </div>
              ))}
            </div>

            <button
              id={`btn-select-plan-${plan.id.toLowerCase().replace(/[^a-z]/g, '-')}`}
              onClick={plan.onClick || (() => onSelectPlan(plan.id))}
              disabled={currentPlan === plan.id}
              className={`w-full py-5 rounded-[2rem] text-[10px] font-black uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-2 group/btn ${
                plan.highlight 
                  ? 'bg-indigo-600 text-white shadow-xl shadow-indigo-600/20 hover:bg-indigo-700 hover:scale-[1.02]' 
                  : 'bg-white text-slate-950 hover:bg-slate-50 hover:scale-[1.02]'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {currentPlan === plan.id ? 'Plano Atual' : (
                <>
                  {plan.buttonText} <ArrowRight size={14} className="group-hover/btn:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </motion.div>
        ))}
      </div>

      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="mt-16 flex flex-col md:flex-row items-center justify-center gap-8 text-slate-500"
      >
        <div className="flex items-center gap-3">
          <Shield className="text-emerald-500" size={18} />
          <span className="text-[10px] font-black uppercase tracking-widest">Plataforma Segura Beta</span>
        </div>
        <div className="flex items-center gap-3">
          <Info className="text-indigo-400" size={18} />
          <span className="text-[10px] font-black uppercase tracking-widest text-center">Seus dados estão protegidos conforme a LGPD</span>
        </div>
      </motion.div>
    </div>
  );
};

export default PricingPanel;
